import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Target } from "lucide-react";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { goalsApi, type Goal, type GoalMessage } from "@/api/goals";
import { queryKeys } from "@/lib/query-keys";
import { MobileAvatar } from "@/components/mobile/mobile-avatar";
import { MobileScreenHeader } from "@/components/mobile/mobile-screen-header";
import { cn } from "@/lib/utils";

type Filter = "all" | "questions" | "blockers" | "updates";

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return d === 1 ? "yesterday" : `${d}d`;
}

const TONE: Record<string, { bar: string; badge: string; badgeText: string; label: string }> = {
  question: { bar: "bg-accent", badge: "bg-accent/10", badgeText: "text-accent", label: "Question" },
  blocker: { bar: "bg-danger", badge: "bg-danger/10", badgeText: "text-danger", label: "Blocker" },
  update: { bar: "bg-[#3B82F6]", badge: "bg-[#3B82F6]/10", badgeText: "text-[#3B82F6]", label: "Update" },
  completion: { bar: "bg-success", badge: "bg-success/10", badgeText: "text-success", label: "Done" },
};

export function MobileInbox() {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const [filter, setFilter] = useState<Filter>("all");

  const { data: goals = [] } = useQuery({
    queryKey: queryKeys.goals.byCompany(tenantId),
    queryFn: () => goalsApi.list(tenantId),
  });

  const ownedGoalIds = useMemo(
    () => goals.filter((g: Goal) => g.ownerId).map((g: Goal) => g.id),
    [goals],
  );

  const { data: allMessages = [] } = useQuery({
    queryKey: ["goal-messages-inbox", tenantId],
    queryFn: async () => {
      const msgs: GoalMessage[] = [];
      for (const gid of ownedGoalIds) {
        msgs.push(...(await goalsApi.listMessages(gid)));
      }
      return msgs;
    },
    enabled: ownedGoalIds.length > 0,
    refetchInterval: 30000,
  });

  const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const recent = useMemo(
    () =>
      allMessages
        .filter(
          (m) =>
            m.direction === "agent_to_human" &&
            (m.createdAt > oneDayAgo || !m.readAt),
        )
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 30),
    [allMessages, oneDayAgo],
  );

  const counts = useMemo(() => {
    const c = { all: recent.length, questions: 0, blockers: 0, updates: 0 };
    recent.forEach((m) => {
      if (m.messageType === "question") c.questions += 1;
      else if (m.messageType === "blocker") c.blockers += 1;
      else c.updates += 1;
    });
    return c;
  }, [recent]);

  const visible = useMemo(() => {
    if (filter === "all") return recent;
    if (filter === "questions") return recent.filter((m) => m.messageType === "question");
    if (filter === "blockers") return recent.filter((m) => m.messageType === "blocker");
    return recent.filter((m) => m.messageType !== "question" && m.messageType !== "blocker");
  }, [recent, filter]);

  const unreadCount = recent.filter((m) => !m.readAt).length;
  const getAgent = (id: string | null) => agents.find((a) => a.id === id);
  const getGoalTitle = (id: string) => goals.find((g: Goal) => g.id === id)?.title || "Goal";

  const markRead = useMutation({
    mutationFn: (goalId: string) => goalsApi.markMessagesRead(goalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goal-messages-inbox", tenantId] });
    },
  });

  const filters: { key: Filter; label: string; n: number }[] = [
    { key: "all", label: "All", n: counts.all },
    { key: "questions", label: "Questions", n: counts.questions },
    { key: "blockers", label: "Blockers", n: counts.blockers },
    { key: "updates", label: "Updates", n: counts.updates },
  ];

  return (
    <div className="flex h-full flex-col">
      <MobileScreenHeader
        title="Inbox"
        subtitle={
          recent.length === 0
            ? "All clear"
            : `${recent.length} recent · ${unreadCount} unread`
        }
        tall
      />

      {/* Segmented control: field-tier track, white active thumb */}
      <div className="px-4 pb-2 pt-3">
        <div className="glass-field flex gap-1 rounded-[13px] p-[3px]">
          {filters.map((f) => {
            const active = f.key === filter;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={cn(
                  "flex h-8 flex-1 items-center justify-center gap-1 rounded-[10px] text-[12px] font-medium transition-colors",
                  active ? "mg-seg-active text-text-primary" : "text-text-muted",
                )}
              >
                {f.label}
                <span
                  className={cn(
                    "flex min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-bold",
                    active ? "text-accent" : "text-text-muted",
                  )}
                  style={active ? { background: "var(--mg-accent-soft)" } : undefined}
                >
                  {f.n}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="min-h-0 flex-1 overflow-auto px-4 pt-1"
        style={{ paddingBottom: "calc(96px + env(safe-area-inset-bottom, 0px))" }}
      >
        {visible.length === 0 ? (
          <div className="glass-card mt-10 rounded-[28px] p-6 text-center text-[13px] text-text-muted">
            {recent.length === 0 ? "All clear. Nothing needs your attention." : "Nothing in this filter."}
          </div>
        ) : (
          visible.map((m) => {
            const tone = TONE[m.messageType] ?? TONE.update;
            const agent = getAgent(m.agentId);
            const needsAction = m.messageType === "question" || m.messageType === "blocker";
            return (
              <div
                key={m.id}
                className="glass-card relative mb-2.5 overflow-hidden rounded-[28px] p-3.5"
              >
                <span className={cn("absolute inset-y-0 left-0 w-[3px]", tone.bar)} />
                <div className="mb-2 flex items-center gap-2.5">
                  <MobileAvatar name={agent?.name || "Agent"} avatarUrl={agent?.avatarUrl} size={34} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-semibold tracking-tight text-text-primary">
                      {agent?.name || "Agent"}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1 text-[11px] text-text-muted">
                      <Target size={11} /> {getGoalTitle(m.goalId)}
                    </div>
                  </div>
                  <div className="text-right">
                    <span
                      className={cn(
                        "inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                        tone.badge,
                        tone.badgeText,
                      )}
                    >
                      {tone.label}
                    </span>
                    <div className="mt-1 text-[11px] text-text-muted">{relativeTime(m.createdAt)}</div>
                  </div>
                </div>
                <div className="text-[14px] leading-snug text-text-secondary line-clamp-3">
                  {m.content}
                </div>
                {needsAction && (
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => navigate(`/tenants/${tenantId}/inbox`)}
                      className="mg-accent-fill h-[34px] flex-1 rounded-full text-[13px] font-semibold"
                    >
                      {m.messageType === "blocker" ? "Unblock" : "Answer"}
                    </button>
                    <button
                      onClick={() => {
                        if (!m.readAt) markRead.mutate(m.goalId);
                        navigate(`/tenants/${tenantId}/goals/${m.goalId}`);
                      }}
                      className="glass-field h-[34px] rounded-full px-3.5 text-[13px] font-medium text-text-primary"
                    >
                      Open thread
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
